import React, { useState, useEffect } from 'react';
import api from '@/config/axios';
import { TrendingUp, DollarSign, Trash2 } from 'lucide-react';
import { useToastContext } from '../ui/feedback/ToastProvider';
import { useConfirmContext } from '../ui/feedback/ConfirmProvider';
import { KPICard, Card, Spinner } from '../ui';
import DirhamSymbol from '../components/DirhamSymbol';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const AdminProfit = () => {
  const { error: showError, success: showSuccess } = useToastContext();
  const { confirm } = useConfirmContext();
  const [adminProfits, setAdminProfits] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [selectedAdmin, setSelectedAdmin] = useState('');
  const [loading, setLoading] = useState(true);
  const [chartData, setChartData] = useState([]);

  const [adminKpis, setAdminKpis] = useState(null);

  useEffect(() => {
    fetchAdmins();
    fetchAdminProfits();
    fetchAdminKpis();
  }, []);

  const fetchAdminKpis = async () => {
    try {
      const response = await api.get('/api/dashboard/kpis');
      setAdminKpis(response.data.kpis);
    } catch (error) {
      console.error('Error fetching admin KPIs:', error);
    }
  };

  useEffect(() => {
    generateChartData();
  }, [adminProfits, selectedAdmin]);

  const fetchAdmins = async () => {
    try {
      const response = await api.get('/api/users');
      const adminUsers = response.data.users.filter(u => u.role === 'admin');
      setAdmins(adminUsers);
      // Set default selected admin to first one if exists
      if (adminUsers.length > 0) {
        setSelectedAdmin(adminUsers[0]._id);
      }
    } catch (error) {
      console.error('Error fetching admins:', error);
    }
  };

  const fetchAdminProfits = async () => {
    try {
      const response = await api.get('/api/admin-profit');
      setAdminProfits(response.data.adminProfits);
    } catch (error) {
      console.error('Error fetching admin profits:', error);
      showError('Failed to fetch admin profits');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (adminProfit) => {
    const confirmed = await confirm({
      title: 'Delete Admin Profit Record',
      message: `Are you sure you want to delete this profit record for ${adminProfit.admin_name}? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
    });

    if (!confirmed) return;

    try {
      await api.delete(`/api/admin-profit/${adminProfit._id}`);
      showSuccess('Admin profit record deleted successfully');
      fetchAdminProfits();
      fetchAdminKpis(); // Refresh KPIs too
    } catch (error) {
      console.error('Error deleting admin profit:', error);
      showError(error.response?.data?.message || 'Failed to delete admin profit record');
    }
  };

  const generateChartData = () => {
    // Group admin profits by month and create timeseries data
    const groupedData = {};

    adminProfits.forEach(ap => {
      const date = new Date(ap.createdAt || ap.updatedAt);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

      if (!groupedData[monthKey]) {
        groupedData[monthKey] = {};
      }

      if (!groupedData[monthKey][ap.admin_id]) {
        groupedData[monthKey][ap.admin_id] = { total_profit_share: 0, available_balance: 0 };
      }

      groupedData[monthKey][ap.admin_id].total_profit_share += ap.total_profit_share || 0;
      groupedData[monthKey][ap.admin_id].available_balance += ap.available_balance || 0;
    });

    const data = Object.keys(groupedData)
      .sort()
      .map(month => {
        const monthData = groupedData[month];
        const entry = { month };

        // Add data for selected admin if exists
        if (selectedAdmin && monthData[selectedAdmin]) {
          entry.profitShare = monthData[selectedAdmin].total_profit_share;
          entry.availableBalance = monthData[selectedAdmin].available_balance;
        } else if (selectedAdmin === 'all') {
          // Aggregate all admins
          let totalProfit = 0;
          let totalAvailable = 0;
          Object.keys(monthData).forEach(adminId => {
            totalProfit += monthData[adminId].total_profit_share;
            totalAvailable += monthData[adminId].available_balance;
          });
          entry.profitShare = totalProfit;
          entry.availableBalance = totalAvailable;
        }

        return entry;
      })
      .filter(entry => entry.profitShare !== undefined);

    setChartData(data);
  };

  // Use live KPI data if available, otherwise fallback to stored sum
  const totalProfit = adminKpis?.totalAdminProfitShare ?? adminProfits.reduce((sum, ap) => sum + (ap.total_profit_share || 0), 0);
  const totalAvailable = adminKpis?.totalAdminAvailable ?? adminProfits.reduce((sum, ap) => sum + (ap.available_balance || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  const selectedAdminUser = admins.find(a => a._id === selectedAdmin);
  const selectedAdminName = selectedAdminUser
    ? `${selectedAdminUser.firstName} ${selectedAdminUser.lastName}`
    : 'All Admins';

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div className="flex items-center gap-3 sm:gap-4">
          <TrendingUp className="text-primary-600 dark:text-primary-400" size={28} />
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-primary-900 dark:text-primary-50">
              Admin Profit
            </h1>
            <p className="text-primary-600 dark:text-primary-400 mt-1.5 text-sm sm:text-base">
              Track admin profit shares
            </p>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5 lg:gap-6">
        <KPICard
          title="Total Profit Share"
          value={
            <span className="flex items-center gap-1">
              <DirhamSymbol size={20} inline />
              {totalProfit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          }
          icon={<TrendingUp size={24} />}
          color="primary"
        />
        <KPICard
          title="Total Available Balance"
          value={
            <span className="flex items-center gap-1">
              <DirhamSymbol size={20} inline />
              {totalAvailable.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          }
          icon={<DollarSign size={24} />}
          color="secondary"
        />
      </div>

      {/* Admin Selection and Chart - Only show if multiple admins */}
      {admins.length > 1 && (
        <Card className="p-6">
          <div className="mb-6">
            <label className="block text-sm font-medium text-primary-700 dark:text-primary-300 mb-2">
              Select Admin
            </label>
            <select
              value={selectedAdmin}
              onChange={(e) => setSelectedAdmin(e.target.value)}
              className="w-full px-4 py-2.5 rounded-lg border border-primary-200 dark:border-primary-700 bg-white dark:bg-primary-800 text-primary-900 dark:text-primary-50 placeholder-primary-400 dark:placeholder-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-transparent transition-all"
            >
              <option value="all">All Admins</option>
              {admins.map(admin => (
                <option key={admin._id} value={admin._id}>
                  {admin.firstName} {admin.lastName}
                </option>
              ))}
            </select>
          </div>

          {chartData.length > 0 ? (
            <div className="w-full h-80">
              <h3 className="text-lg font-semibold text-primary-900 dark:text-primary-50 mb-4">
                {selectedAdminName} - Profit Trend
              </h3>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="currentColor" />
                  <XAxis
                    dataKey="month"
                    stroke="currentColor"
                  />
                  <YAxis
                    stroke="currentColor"
                  />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '8px' }}
                    formatter={(value) => value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="profitShare"
                    stroke="#3b82f6"
                    name="Profit Share (AED)"
                    strokeWidth={2}
                    dot={{ fill: '#3b82f6', r: 4 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="availableBalance"
                    stroke="#10b981"
                    name="Available Balance (AED)"
                    strokeWidth={2}
                    dot={{ fill: '#10b981', r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-primary-600 dark:text-primary-400">No chart data available</p>
            </div>
          )}
        </Card>
      )}

      {/* Admin Profit Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5 lg:gap-6">
        {adminProfits.map((ap) => {
          // Look up live calculated data from adminKpis if available
          const liveAdminData = adminKpis?.admins?.find(a => a.name === ap.admin_name);
          const displayShare = liveAdminData ? liveAdminData.share : (ap.total_profit_share || 0);
          const displayAvailable = liveAdminData ? liveAdminData.available : (ap.available_balance || 0);

          return (
            <Card key={ap._id} className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">{ap.admin_name}</h3>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleDelete(ap)}
                    className="p-2 text-error-600 dark:text-error-400 hover:text-error-800 dark:hover:text-error-200 hover:bg-error-100 dark:hover:bg-error-900/20 rounded-lg transition-colors"
                    title="Delete"
                  >
                    <Trash2 size={18} />
                  </button>
                  <div className="w-12 h-12 rounded-full bg-primary-600 flex items-center justify-center text-white font-bold">
                    {ap.admin_name === 'Admin A' ? 'A' : 'B'}
                  </div>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <span className="text-gray-600 dark:text-gray-400">Total Profit Share</span>
                  <span className="font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-1">
                    <DirhamSymbol size={16} inline />
                    {displayShare.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <span className="text-gray-600 dark:text-gray-400">Available Balance</span>
                  <span className="font-semibold text-green-600 dark:text-green-400 flex items-center gap-1">
                    <DirhamSymbol size={16} inline />
                    {displayAvailable.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
                <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Last updated: {new Date(ap.updatedAt).toLocaleString()}
                  </p>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {adminProfits.length === 0 && (
        <Card className="text-center py-8">
          <p className="text-primary-600 dark:text-primary-400">No admin profit records found</p>
        </Card>
      )}

    </div>
  );
};

export default AdminProfit;
